/**
 * SidebarLogoAnomalyDot — Phase G · Active Anomaly Indicator
 *
 * A discrete pulsating dot rendered absolutely-positioned over the
 * Sidebar logo. Visible ONLY when `usePipelineStats` reports
 * `hasAnomaly === true` (i.e. Modal credentials missing OR >5 failed
 * jobs in the last 24h).
 *
 * Design intent: keep the passive Pipeline Health Banner permanent
 * (always visible, never noisy) while THIS dot acts as a true
 * "something needs your attention" signal — like the OS-level red
 * badge on a tab. Hovering reveals the precise anomaly reason(s) so
 * the admin doesn't need to mouse-over the banner to figure out
 * what's broken.
 *
 * Animation: Tailwind's built-in `animate-ping` (slow scale + fade)
 * layered behind a solid rose dot so the eye catches movement
 * without distracting motion in the main UI.
 */
import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import usePipelineStats from "@/hooks/usePipelineStats";

export default function SidebarLogoAnomalyDot() {
  const { hasAnomaly, anomalyReasons } = usePipelineStats();

  if (!hasAnomaly) {
    // Render-nothing fast-path keeps the logo crisp 99 % of the time.
    return null;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="sidebar-logo-anomaly-dot"
            className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
            aria-label="Pipeline anomaly detected"
          >
            {/* Outer ping — smooth scale + fade, behind the solid dot. */}
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500/70" />
            {/* Inner solid dot — what the eye actually locks onto. */}
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 border border-rose-300/40" />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          className="bg-background border border-rose-500/40 p-2.5 shadow-lg max-w-xs"
        >
          <div
            data-testid="sidebar-logo-anomaly-tooltip"
            className="space-y-1.5"
          >
            <div className="font-display uppercase tracking-widest text-[10px] text-rose-400">
              Pipeline Anomaly
            </div>
            {anomalyReasons.map((r) => (
              <div
                key={r.code}
                data-testid={`logo-anomaly-reason-${r.code}`}
                className="text-[10px] font-mono text-foreground leading-snug"
              >
                ⚠️ {r.message}
              </div>
            ))}
            <div className="text-[9px] font-mono text-muted-foreground pt-1 border-t border-border">
              Open the health banner below for the 24 h breakdown.
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
